import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getUnitLessons, getUnitProgression, getCurriculumConfig, UnitLessonsData, ProgressionData } from '../services/api';
import { storage } from '../services/storage';
import { PracticePage } from './PracticePage';
import { ReviewPage } from './ReviewPage';

export const LessonSelectPage: React.FC = () => {
  const { unit } = useParams<{ unit?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode');
  const unitFromUrl = unit ? parseInt(unit) : null;

  const [selectedUnit, setSelectedUnit] = useState<number | null>(unitFromUrl);
  const [unitData, setUnitData] = useState<UnitLessonsData | null>(null);
  const [progression, setProgression] = useState<ProgressionData | null>(null);
  const [units, setUnits] = useState<{ number: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const studentId = storage.getStudentId();
  const isRemediation = mode === 'remediation';
  const isReviewOnly = mode === 'review_only';
  const isChallenge = mode === 'challenge';

  // Load curriculum config and progression data on mount (skip for special modes)
  useEffect(() => {
    if (isRemediation || isReviewOnly || isChallenge) return;
    getCurriculumConfig(studentId)
      .then(cfg => {
        const unitList = [];
        for (let i = 1; i <= cfg.total_units; i++) {
          unitList.push({ number: i, name: cfg.unit_names[String(i)] || `Unit ${i}` });
        }
        setUnits(unitList);
      })
      .catch(console.error);
    getUnitProgression(studentId)
      .then(setProgression)
      .catch(console.error);
  }, [studentId, isRemediation, isReviewOnly, isChallenge]);

  // Sync with URL parameter
  useEffect(() => {
    if (isRemediation || isReviewOnly || isChallenge) return;
    if (unitFromUrl !== selectedUnit) {
      setSelectedUnit(unitFromUrl);
    }
  }, [unitFromUrl, selectedUnit, isRemediation, isReviewOnly]);

  useEffect(() => {
    if (isRemediation || isReviewOnly || isChallenge) return;
    if (selectedUnit) {
      // Check if unit is unlocked
      if (progression) {
        const unitStatus = progression.units.find(u => u.unit === selectedUnit);
        if (unitStatus && !unitStatus.unlocked) {
          navigate('/practice');
          return;
        }
      }

      setLoading(true);
      getUnitLessons(selectedUnit, studentId)
        .then(setUnitData)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [selectedUnit, studentId, progression, navigate, isRemediation]);

  // If in remediation mode, render PracticePage directly (no unit/lesson selection needed)
  // If in review_only mode, render ReviewPage directly
  // These must come AFTER all hooks to satisfy React's rules of hooks
  if (isRemediation) {
    return <PracticePage />;
  }
  if (isReviewOnly) {
    return <ReviewPage />;
  }
  if (isChallenge) {
    return <PracticePage />;
  }

  const selectUnit = (unitNum: number) => {
    // Check if unit is unlocked
    const unitStatus = progression?.units.find(u => u.unit === unitNum);
    if (unitStatus && !unitStatus.unlocked) {
      alert(`Unit ${unitNum} is locked. Complete Unit ${unitNum - 1} Mastery Gate to unlock.`);
      return;
    }
    navigate(`/practice/${unitNum}`);
  };

  const goBackToUnits = () => {
    navigate('/practice');
    setSelectedUnit(null);
    setUnitData(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="status-icon completed">✓</span>;
      case 'in_progress':
        return <span className="status-icon in-progress">◐</span>;
      default:
        return <span className="status-icon not-started">○</span>;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Not Started';
    }
  };

  const isUnitUnlocked = (unitNum: number) => {
    if (!progression) return true; // Default to unlocked while loading
    const unitStatus = progression.units.find(u => u.unit === unitNum);
    return unitStatus?.unlocked ?? true;
  };

  const isUnitPassed = (unitNum: number) => {
    if (!progression) return false;
    const unitStatus = progression.units.find(u => u.unit === unitNum);
    return unitStatus?.passed_gate ?? false;
  };

  // Unit selection view
  if (!selectedUnit) {
    return (
      <div className="lesson-select-page">
        <h1>Choose a Unit</h1>
        <div className="units-grid">
          {units.map(u => {
            const unlocked = isUnitUnlocked(u.number);
            const passed = isUnitPassed(u.number);
            return (
              <button
                key={u.number}
                className={`unit-card ${!unlocked ? 'locked' : ''} ${passed ? 'passed' : ''}`}
                onClick={() => selectUnit(u.number)}
                disabled={!unlocked}
              >
                {!unlocked && <span className="lock-badge">🔒</span>}
                {passed && <span className="pass-badge">✓</span>}
                <div className="unit-number">Unit {u.number}</div>
                <h3 className="unit-name">{u.name}</h3>
                {!unlocked && (
                  <div className="unlock-hint">
                    Pass Unit {u.number - 1} Gate
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <Link to="/" className="back-link">← Back to Dashboard</Link>
      </div>
    );
  }

  // Lesson selection view
  const unitInfo = units.find(u => u.number === selectedUnit);
  const unitPassed = isUnitPassed(selectedUnit);

  return (
    <div className="lesson-select-page">
      <div className="lesson-select-header">
        <button className="back-btn" onClick={goBackToUnits}>
          ← Units
        </button>
        <h1>
          {unitPassed && <span className="header-pass-badge">✓</span>}
          Unit {selectedUnit}: {unitInfo?.name}
        </h1>
      </div>

      {loading ? (
        <div className="loading">Loading lessons...</div>
      ) : unitData ? (
        <>
          <div className="lessons-grid">
            {unitData.lessons.map(lesson => (
              <Link
                key={lesson.lesson}
                to={`/practice/${selectedUnit}/${lesson.lesson}`}
                className={`lesson-card ${lesson.status}`}
              >
                <div className="lesson-header">
                  {getStatusIcon(lesson.status)}
                  <span className="lesson-number">Lesson {lesson.lesson}</span>
                </div>

                <div className="lesson-stats">
                  {lesson.status === 'not_started' ? (
                    <span className="stat-label">{lesson.total_questions} questions</span>
                  ) : (
                    <>
                      <span className="stat-value">
                        {lesson.correct}/{lesson.attempted} correct
                      </span>
                      {lesson.accuracy !== null && (
                        <span className="stat-accuracy">{lesson.accuracy}%</span>
                      )}
                    </>
                  )}
                </div>

                <div className="lesson-status-label">
                  {getStatusLabel(lesson.status)}
                </div>
              </Link>
            ))}
          </div>

          {unitData.next_lesson && !unitPassed && (
            <div className="continue-section">
              <Link
                to={`/practice/${selectedUnit}/${unitData.next_lesson}`}
                className="continue-btn"
              >
                Continue with Lesson {unitData.next_lesson} →
              </Link>
            </div>
          )}

          <Link
            to={`/mastery-gate/${selectedUnit}`}
            className={`gate-link-btn ${unitPassed ? 'passed' : ''}`}
          >
            {unitPassed ? '✓ Mastery Gate Passed' : 'Take Mastery Gate Assessment'}
          </Link>
        </>
      ) : (
        <div className="error">Failed to load lessons</div>
      )}

      <Link to="/" className="back-link">← Back to Dashboard</Link>
    </div>
  );
};
